import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  Inspection,
  InspectionSummary,
  PaginatedResponse,
  CreateInspectionPayload,
  InspectionResult,
  SignOffInspectionPayload,
  BatchInspectPayload,
  BatchInspectResponse,
} from '@/lib/types';

export interface InspectionFilters {
  page?: number;
  page_size?: number;
  result?: InspectionResult | '';
  date_after?: string;
  date_before?: string;
  asset?: number | '';
  ordering?: string;
}

export function useInspections(filters: InspectionFilters = {}) {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );

  return useQuery({
    queryKey: queryKeys.inspections.list(cleanFilters),
    queryFn: async (): Promise<PaginatedResponse<Inspection>> => {
      const response = await apiClient.get<PaginatedResponse<Inspection>>('/api/v1/inspections/', {
        params: cleanFilters,
      });
      return response.data;
    },
  });
}

export function useInspection(id: number) {
  return useQuery({
    queryKey: queryKeys.inspections.detail(id),
    queryFn: async (): Promise<Inspection> => {
      const response = await apiClient.get<Inspection>(`/api/v1/inspections/${id}/`);
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useInspectionSummary(filters: Omit<InspectionFilters, 'page' | 'page_size' | 'ordering'> = {}) {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );

  return useQuery({
    queryKey: [...queryKeys.inspections.summary(), cleanFilters],
    queryFn: async (): Promise<InspectionSummary> => {
      const response = await apiClient.get<InspectionSummary>('/api/v1/inspections/summary/', {
        params: cleanFilters,
      });
      return response.data;
    },
  });
}

export function useCreateInspection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateInspectionPayload): Promise<Inspection> => {
      const response = await apiClient.post<Inspection>('/api/v1/inspections/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inspections.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    },
  });
}

export function useUpdateInspection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<CreateInspectionPayload> }): Promise<Inspection> => {
      const response = await apiClient.patch<Inspection>(`/api/v1/inspections/${id}/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inspections.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.inspections.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    },
  });
}

export function useDeleteInspection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`/api/v1/inspections/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inspections.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    },
  });
}

export function useAmendInspection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data?: { result?: string; notes?: string } }): Promise<Inspection> => {
      const response = await apiClient.post<Inspection>(`/api/v1/inspections/${id}/amend/`, data ?? {});
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inspections.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    },
  });
}

export function useSignOffInspection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: SignOffInspectionPayload }): Promise<Inspection> => {
      const response = await apiClient.post<Inspection>(`/api/v1/inspections/${id}/sign-off/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inspections.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.inspections.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    },
  });
}

export function useBatchInspect() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BatchInspectPayload): Promise<BatchInspectResponse> => {
      const response = await apiClient.post<BatchInspectResponse>(
        '/api/v1/inspections/bulk_inspect/',
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inspections.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    },
  });
}

export function useExportInspectionsCsv() {
  return useMutation({
    mutationFn: async (filters: Omit<InspectionFilters, 'page' | 'page_size'> = {}): Promise<Blob> => {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
      );
      const response = await apiClient.get('/api/v1/export/inspections.csv/', {
        params: cleanFilters,
        responseType: 'blob',
      });
      return response.data;
    },
  });
}
